import { ACCESS } from '@/features/access/access';
import { RoleGate } from '@/features/access/RoleGate';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { PlanCard } from '@/features/billing/PlanCard';
import { PlanCardRailMeter } from '@/features/billing/PlanCardUsage';
import { useSubscriptionPaused } from '@/features/billing/use-subscription-paused';
import { Plus } from '@/icons/Icon';

/**
 * The sidebar's workspace card: the switcher, today's sending meter and plan
 * lines (`PlanCard`), and a "Connect mailbox" action into
 * `/email-accounts/connect`.
 *
 * Collapsed, it shrinks to the switcher tile, a bar-only meter
 * (`PlanCardRailMeter`) and a "+" button. Connect is disabled — never
 * unmounted — while the subscription is paused, with the paused reason as its
 * tooltip (apps/web/CLAUDE.md).
 */
export function SidebarWorkspaceCard({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const { reason: pausedReason } = useSubscriptionPaused();
  const connect = () => void navigate({ to: '/email-accounts/connect' });

  const connectButton = (
    <button
      type="button"
      disabled={pausedReason != null}
      onClick={connect}
      aria-label={collapsed ? 'Connect mailbox' : undefined}
      className={cn('sidebar-connect', collapsed && 'is-collapsed')}
    >
      <Plus size={collapsed ? 15 : 14} strokeWidth={2.2} />
      {!collapsed && 'Connect mailbox'}
    </button>
  );

  // A disabled <button> fires no pointer events, so the tooltip anchors on a
  // wrapper span (same shape as `DisabledReasonButton`).
  const tooltip = pausedReason ?? (collapsed ? 'Connect mailbox' : null);
  const connectControl = (
    <RoleGate {...ACCESS.connectAccount}>
      {tooltip ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('flex', !collapsed && 'w-full')} tabIndex={pausedReason ? 0 : undefined}>
          {connectButton}
        </span>
      </TooltipTrigger>
      <TooltipContent side={collapsed ? 'right' : 'bottom'} sideOffset={collapsed ? 10 : 6}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
      ) : (
        connectButton
      )}
    </RoleGate>
  );

  if (collapsed) {
    return (
      <div className="sidebar-ws-rail">
        <div className="w-[30px]">
          <WorkspaceSwitcher collapsed />
          <PlanCardRailMeter />
        </div>
        {connectControl}
      </div>
    );
  }

  return (
    <div className="sidebar-ws-wrap">
      <div className="sidebar-ws-card">
        <WorkspaceSwitcher />
        <div className="sidebar-ws-body">
          <PlanCard />
        </div>
        {connectControl}
      </div>
    </div>
  );
}
